import { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';

import useMailerDriver from 'src/modules/mailer/hooks/useMailerDriver';
import useMailerSettingsService from 'src/modules/mailer/hooks/useMailerSettingsService';
import { MAILER_SETTINGS_QUERY_KEY } from '../../../hooks/useEmailSettings';
import {
    extractApiMessage,
    isIncomingValidateUnavailable,
    mapMicrosoftOAuthMessage,
} from '../../../helpers';
import {
    MICROSOFT_OAUTH_POPUP_FEATURES,
    MICROSOFT_OAUTH_POPUP_NAME,
    subscribeMicrosoftOAuthReturn,
} from '../../../microsoft-oauth-channel';

const getAuthorizeUrl = (data) =>
    data?.authorizeUrl || data?.authorize_url || data?.url;

const openOAuthPopup = () =>
    window.open('about:blank', MICROSOFT_OAUTH_POPUP_NAME, MICROSOFT_OAUTH_POPUP_FEATURES);

const useMicrosoftOAuth = ({
    t,
    enabled = true,
    onSettingsRefresh,
}) => {
    const history = useHistory();
    const location = useLocation();
    const queryClient = useQueryClient();
    const { active, settings } = useMailerDriver();
    const service = useMailerSettingsService();
    const settingsQueryKey = [
        settings.settingsQueryKey || MAILER_SETTINGS_QUERY_KEY,
        active,
    ];
    const callbackHandledRef = useRef(false);
    const popupRef = useRef(null);
    const [isWaitingForPopup, setIsWaitingForPopup] = useState(false);
    const [showDisconnectModal, setShowDisconnectModal] = useState(false);

    const refreshSettings = (overrides) => {
        if (onSettingsRefresh) {
            return onSettingsRefresh(overrides);
        }

        return queryClient.invalidateQueries(settingsQueryKey);
    };

    const handleOAuthResult = (payload) => {
        if (callbackHandledRef.current) {
            return;
        }

        callbackHandledRef.current = true;
        setIsWaitingForPopup(false);
        popupRef.current = null;

        const message = payload?.message;

        if (payload?.oauth === 'success') {
            toast.success(message || t('Microsoft mailbox connected.'));
            refreshSettings({ oauthConnected: true });
            validateIncomingMutation.mutate();
            return;
        }

        toast.error(
            mapMicrosoftOAuthMessage(message, t) ||
                message ||
                t('An error occurred while connecting Microsoft.')
        );
        refreshSettings();
    };

    const startMutation = useMutation({
        mutationFn: async () => {
            return await service.startMicrosoftOAuth();
        },
        onSuccess: (data) => {
            const authorizeUrl = getAuthorizeUrl(data);
            const popup = popupRef.current;

            if (!authorizeUrl) {
                popup?.close();
                setIsWaitingForPopup(false);
                toast.error(t('An error occurred while starting Microsoft sign-in.'));
                return;
            }

            if (!popup || popup.closed) {
                setIsWaitingForPopup(false);
                toast.error(t('Allow popups for this site to connect Microsoft.'));
                return;
            }

            popup.location.assign(authorizeUrl);
        },
        onError: (error) => {
            popupRef.current?.close();
            setIsWaitingForPopup(false);

            if (process.env.NODE_ENV === 'development') console.error(error);

            const rawMessage = extractApiMessage(error);

            toast.error(
                mapMicrosoftOAuthMessage(rawMessage, t) ||
                    rawMessage ||
                    t('An error occurred while starting Microsoft sign-in.')
            );
        },
    });

    const disconnectMutation = useMutation({
        mutationFn: async () => {
            return await service.disconnectMicrosoftOAuth();
        },
        onSuccess: () => {
            setShowDisconnectModal(false);
            toast.success(t('Microsoft mailbox disconnected.'));
            refreshSettings({ oauthConnected: false });
        },
        onError: (error) => {
            if (process.env.NODE_ENV === 'development') console.error(error);

            const rawMessage = extractApiMessage(error);

            toast.error(
                mapMicrosoftOAuthMessage(rawMessage, t) ||
                    rawMessage ||
                    t('An error occurred while disconnecting Microsoft.')
            );
        },
    });

    const validateIncomingMutation = useMutation({
        mutationFn: async () => {
            return await service.validateIncoming({});
        },
        onError: (error) => {
            if (process.env.NODE_ENV === 'development') console.error(error);

            if (isIncomingValidateUnavailable(error)) {
                return;
            }

            const rawMessage = extractApiMessage(error);

            toast.error(
                mapMicrosoftOAuthMessage(rawMessage, t) ||
                    rawMessage ||
                    t('An error occurred while validating the Microsoft mailbox.')
            );
        },
    });

    const startConnect = () => {
        const popup = openOAuthPopup();

        if (!popup) {
            toast.error(t('Allow popups for this site to connect Microsoft.'));
            return;
        }

        callbackHandledRef.current = false;
        popupRef.current = popup;
        setIsWaitingForPopup(true);
        startMutation.mutate();
    };

    useEffect(() => {
        if (!isWaitingForPopup) {
            return;
        }

        const timer = window.setInterval(() => {
            if (popupRef.current?.closed) {
                setIsWaitingForPopup(false);
                popupRef.current = null;
            }
        }, 500);

        return () => window.clearInterval(timer);
    }, [isWaitingForPopup]);

    useEffect(() => {
        if (!enabled) {
            return;
        }

        return subscribeMicrosoftOAuthReturn(handleOAuthResult);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled]);

    useEffect(() => {
        if (!enabled || callbackHandledRef.current) {
            return;
        }

        const params = new URLSearchParams(location.search);
        const oauth = params.get('oauth');

        if (!oauth) {
            return;
        }

        const provider = params.get('provider');
        if (provider && provider !== 'microsoft') {
            return;
        }

        handleOAuthResult({
            oauth,
            provider: provider || 'microsoft',
            message: params.get('message'),
        });

        params.delete('oauth');
        params.delete('message');
        if (provider === 'microsoft') {
            params.delete('provider');
        }
        if (!params.get('tab')) {
            params.set('tab', 'email-settings');
        }

        history.replace({
            pathname: location.pathname,
            search: params.toString(),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, location.search]);

    return {
        isConnecting: startMutation.isLoading || isWaitingForPopup,
        isDisconnecting: disconnectMutation.isLoading,
        showDisconnectModal,
        setShowDisconnectModal,
        handleConnect: startConnect,
        handleReconnect: startConnect,
        handleConfirmDisconnect: () => disconnectMutation.mutate(),
    };
};

export default useMicrosoftOAuth;
